import React, { useEffect, useState } from 'react';
import { Modal, ModalProps, ModalRef } from '../../../../../patterns/01-molecules/modal/modal.tsx';
import { RestaurantDetails_getRestaurantDetails } from '../../../../../types/graphql-types-generated.ts';
import { ModalBackButton } from '../../../../../patterns/01-molecules/modal/modal-back-button.tsx';
import { classList } from '../../../../../utils/string.ts';
import { negateModalPaddingClasses } from '../../../../../patterns/01-molecules/modal/negate-modal-padding.tsx';
import { RestaurantInfoModalOverview } from './restaurant-info-modal-overview.tsx';
import { RestaurantInfoModalReviews } from './restaurant-info-modal-reviews.tsx';
import { RestaurantInfoModalMap } from './restaurant-info-modal-map.tsx';
import { RestaurantInfoModalReservation } from './restaurant-info-modal-reservation.tsx';
import { modalScrollAreaClasses } from '../../../../../patterns/01-molecules/modal/modal-scroll-area.tsx';
import { UBTracking } from '../../../../../components/tracking/ubtracking.ts';
import { Cookie } from '../../../../../utils/cookie.ts';

export type RestaurantInfoModalView =
  | { type: 'overview' }
  | { type: 'map' }
  | { type: 'reserve' }
  | { type: 'reviews' };

export const horizontalLine = (options?: { className?: string }) => (
  <hr
    className={classList(
      'text-sk-grey-200 mb-4 border-2',
      negateModalPaddingClasses(),
      options?.className
    )}
  />
);

export type RestaurantInfoModalProps = {
  restaurantDetails: RestaurantDetails_getRestaurantDetails;
  modalDefaultView?: RestaurantInfoModalView;
  onClose?: () => void;
  showAds?: boolean;
};

export const RestaurantInfoModal = React.forwardRef<ModalRef, RestaurantInfoModalProps>(
  (props, ref) => {
    const abTabSetup = new Cookie('sk-AB-tabsetup').value ?? 0;
    const { restaurantDetails, modalDefaultView, showAds } = props;

    const [view, setView] = useState<RestaurantInfoModalView>({ type: 'overview' });

    useEffect(() => {
      modalDefaultView && setView(modalDefaultView);
    }, [modalDefaultView]);

    const content = (children: React.ReactNode) => (
      <>
        <div className="flex items-center">
          <i className="ic-kitchen-small mr-1 block text-base" />
          <span className="">
            {restaurantDetails.cuisineTranslations?.map(cuisine => cuisine.name).join(', ')}
          </span>
        </div>

        <div
          className={classList(
            'mt-4',
            modalScrollAreaClasses({ border: { top: true } }),
            negateModalPaddingClasses({ bottom: { negate: true }, horizontal: { negate: true } })
          )}
        >
          {view.type !== 'overview' && abTabSetup !== '4' && (
            <ModalBackButton
              className="mt-4"
              goBack={() => {
                UBTracking.trackEvent.click('restaurant_info_modal', 'back');
                setView({ type: 'overview' });
              }}
            />
          )}

          {children}
        </div>
      </>
    );

    const modalProps = ((): React.PropsWithChildren<ModalProps> => {
      switch (view.type) {
        case 'overview':
          return {
            children: content(
              <RestaurantInfoModalOverview
                restaurantDetails={restaurantDetails}
                onMapClick={() => {
                  UBTracking.trackEvent.click('restaurant_info_modal', 'map');
                  setView({ type: 'map' });
                }}
                onMapLoad={target => {
                  UBTracking.onTrackTargetView(target, 'restaurant_info_modal', 'map');
                }}
                onReserveClick={() => {
                  UBTracking.trackEvent.click('restaurant_info_modal', 'reservation');
                  setView({ type: 'reserve' });
                }}
                onReserveLoad={target => {
                  UBTracking.onTrackTargetView(target, 'restaurant_info_modal', 'reservation');
                }}
                onReviewsClick={() => {
                  UBTracking.trackEvent.click('restaurant_info_modal', 'reviews');
                  setView({ type: 'reviews' });
                }}
                onReviewsLoad={target => {
                  UBTracking.onTrackTargetView(target, 'restaurant_info_modal', 'reviews');
                }}
              />
            )
          };
        case 'map':
          return {
            children: content(<RestaurantInfoModalMap restaurantDetails={restaurantDetails} />)
          };
        case 'reserve':
          return {
            children: content(
              <RestaurantInfoModalReservation restaurantDetails={restaurantDetails} />
            )
          };
        case 'reviews':
          return {
            children: content(<RestaurantInfoModalReviews restaurantDetails={restaurantDetails} />)
          };
      }
    })();

    return (
      <Modal
        {...modalProps}
        headline={restaurantDetails.name}
        ref={ref}
        defaultOpen={false}
        alwaysIncludeInDom={true}
        adaptiveFlexContent={view.type !== 'map'}
        fixHeightToScreen={view.type !== 'map'}
        onClose={() => {
          UBTracking.trackEvent.click('restaurant_info_modal', 'restaurant_info_modal', 'close');
          props.onClose && props.onClose();
          setView({ type: 'overview' });
        }}
        className={classList(abTabSetup === '3' && showAds ? 'mt-28 md:mt-65' : 'mb-32')}
      />
    );
  }
);
